import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";

const PRIORITY_OPTS = [
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "High" },
  { v: "urgent", label: "Urgent" },
];

export default function TaskTemplatesView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, userRole } = useApp();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | template object

  const isAdmin = userRole === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/kanban-templates${isAdmin ? "?includeInactive=1" : ""}`);
      const d = await r.json();
      if (r.ok) setTemplates(Array.isArray(d) ? d : []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>
        Шаблони задач можуть редагувати тільки адміни.
      </div>
    );
  }

  const save = async (data, id) => {
    try {
      const url = id ? `/api/kanban-templates/${id}` : "/api/kanban-templates";
      const r = await apiFetch(url, { method: id ? "PUT" : "POST", body: JSON.stringify(data) });
      const d = await r.json();
      if (r.ok) { flash(id ? "✓ Оновлено" : "✓ Створено"); setEditing(null); await load(); }
      else flash(d.error || "Помилка");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Видалити шаблон?")) return;
    try {
      const r = await apiFetch(`/api/kanban-templates/${id}`, { method: "DELETE" });
      if (r.ok) { flash("✓ Видалено"); await load(); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  return (
    <div style={{ padding: 24, color: t.text, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>📚 Шаблони задач</h1>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
            База готових заготовок для нових задач Канбану. Користувачі обирають їх у "+" → "Створити з шаблону".
          </div>
        </div>
        <button onClick={() => setEditing("new")} style={{ ...btnP, height: 36, padding: "0 16px" }}>+ Новий шаблон</button>
      </div>

      {loading && <div style={{ padding: 20, color: t.textMuted, textAlign: "center" }}>Завантаження…</div>}

      {!loading && templates.length === 0 && (
        <div style={{ padding: 50, textAlign: "center", border: `2px dashed ${t.border}`, borderRadius: 16, marginTop: 8 }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>📚</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 6 }}>Шаблонів ще немає</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 18, maxWidth: 380, margin: "0 auto 18px" }}>
            Створіть перший шаблон ТЗ — він буде доступний у Канбані у кожного проекту через "+ → 📚 База знань".
          </div>
          <button onClick={() => setEditing("new")} style={{ ...btnP, padding: "10px 22px", fontSize: 13 }}>+ Створити перший шаблон</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {templates.map(tpl => (
          <div key={tpl.id} style={{ ...card, padding: 14, position: "relative", opacity: tpl.active === false ? 0.55 : 1 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>{tpl.icon || "📋"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{tpl.name}</div>
                {tpl.title_prefix && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, fontFamily: "monospace" }}>Title: "{tpl.title_prefix}…"</div>}
              </div>
              {tpl.active === false && <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(107,114,128,0.15)", color: "#6b7280" }}>OFF</span>}
            </div>
            {tpl.description && <div style={{ fontSize: 11.5, color: t.textMuted, marginBottom: 10, lineHeight: 1.4 }}>{tpl.description}</div>}
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setEditing(tpl)} style={{ ...btnG, fontSize: 11, padding: "5px 12px" }}>✎ Редагувати</button>
              <button onClick={() => remove(tpl.id)} style={{ background: "transparent", border: `1px solid rgba(239,68,68,0.4)`, color: "#ef4444", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>🗑</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditorModal
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(data) => save(data, editing === "new" ? null : editing.id)}
          dark={dark} t={t} inp={inp} btnP={btnP} btnG={btnG}
        />
      )}
    </div>
  );
}

function EditorModal({ template, onClose, onSave, dark, t, inp, btnP, btnG }) {
  const [name, setName] = useState(template?.name || "");
  const [icon, setIcon] = useState(template?.icon || "📋");
  const [description, setDescription] = useState(template?.description || "");
  const [titlePrefix, setTitlePrefix] = useState(template?.title_prefix || "");
  const [defaultPriority, setDefaultPriority] = useState(template?.default_priority || "medium");
  const [active, setActive] = useState(template?.active !== false);
  const [sortOrder, setSortOrder] = useState(template?.sort_order ?? 0);
  const [body, setBody] = useState(template?.body_html || "");

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#0f0f1c" : "#fff", borderRadius: 14, maxWidth: 860, width: "100%", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>{template ? "✎" : "+"}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{template ? "Редагувати шаблон" : "Новий шаблон"}</div>
            <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Доступно у dropdown'і + у Канбані</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 22, color: t.textMuted, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 120px", gap: 10 }}>
            <div>
              <Lbl t={t}>Іконка</Lbl>
              <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="📋" style={{ ...inp, fontSize: 20, padding: "6px 8px", width: "100%", textAlign: "center", boxSizing: "border-box" }} />
            </div>
            <div>
              <Lbl t={t}>Назва шаблону</Lbl>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="ТЗ — щось" style={{ ...inp, fontSize: 13, padding: "8px 10px", width: "100%", boxSizing: "border-box", fontWeight: 600 }} />
            </div>
            <div>
              <Lbl t={t}>Сортування</Lbl>
              <input type="number" value={sortOrder} onChange={e => setSortOrder(parseInt(e.target.value) || 0)} style={{ ...inp, fontSize: 13, padding: "8px 10px", width: "100%", boxSizing: "border-box" }} />
            </div>
          </div>
          <div>
            <Lbl t={t}>Короткий опис (підказка для менеджера)</Lbl>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Коли і для чого використовується" style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 110px", gap: 10 }}>
            <div>
              <Lbl t={t}>Префікс назви задачі</Lbl>
              <input value={titlePrefix} onChange={e => setTitlePrefix(e.target.value)} placeholder='ТЗ: ' style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%", boxSizing: "border-box", fontFamily: "monospace" }} />
            </div>
            <div>
              <Lbl t={t}>Пріоритет за замовч.</Lbl>
              <select value={defaultPriority} onChange={e => setDefaultPriority(e.target.value)} style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%" }}>
                {PRIORITY_OPTS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: t.text, cursor: "pointer" }}>
                <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Активний
              </label>
            </div>
          </div>
          <div>
            <Lbl t={t}>Тіло шаблону (HTML)</Lbl>
            <RichEditorLite value={body} onChange={setBody} dark={dark} t={t} />
            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>Підтримка лого: вставляйте {"<img src=\"/svitlo-logo.png\" />"} — буде працювати.</div>
          </div>
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${t.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...btnG, fontSize: 12, padding: "8px 16px" }}>Скасувати</button>
          <button
            onClick={() => onSave({ name: name.trim(), icon, body_html: body, description, title_prefix: titlePrefix, default_priority: defaultPriority, sort_order: sortOrder, active })}
            disabled={!name.trim() || !body}
            style={{ ...btnP, fontSize: 12, padding: "8px 22px", opacity: !name.trim() || !body ? 0.5 : 1 }}>
            {template ? "💾 Зберегти" : "✓ Створити"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Lbl({ children, t }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>{children}</div>;
}

// Lightweight rich-text editor (duplicate of KanbanView's, kept local to avoid cross-imports).
function RichEditorLite({ value, onChange, dark, t }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!value && ref.current.innerHTML !== "") ref.current.innerHTML = "";
    if (value && ref.current.innerHTML === "") ref.current.innerHTML = value;
  }, [value]);
  const exec = (cmd, val) => { ref.current?.focus(); document.execCommand(cmd, false, val); if (ref.current) onChange(ref.current.innerHTML); };
  const link = () => { const url = window.prompt("URL:"); if (url) exec("createLink", url); };
  const img = () => { const url = window.prompt("URL картинки (наприклад /svitlo-logo.png):"); if (url) exec("insertImage", url); };
  const Btn = ({ onClick, label, title }) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); onClick(); }} title={title || label}
      style={{ minWidth: 28, height: 28, padding: "0 8px", borderRadius: 5, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, cursor: "pointer", fontSize: 12, fontWeight: 600, color: t.text, fontFamily: "inherit" }}>{label}</button>
  );
  return (
    <div style={{ border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)"}`, borderRadius: 8, overflow: "hidden", background: dark ? "rgba(255,255,255,0.02)" : "#fff" }}>
      <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, flexWrap: "wrap", background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
        <Btn onClick={() => exec("bold")} label={<b>B</b>} title="Жирний" />
        <Btn onClick={() => exec("italic")} label={<i>I</i>} title="Курсив" />
        <Btn onClick={() => exec("underline")} label={<u>U</u>} title="Підкреслено" />
        <Btn onClick={() => exec("strikeThrough")} label={<s>S</s>} title="Закреслено" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={() => exec("formatBlock", "<h2>")} label="H2" title="Заголовок 2" />
        <Btn onClick={() => exec("formatBlock", "<h3>")} label="H3" title="Заголовок 3" />
        <Btn onClick={() => exec("formatBlock", "<p>")} label="¶" title="Звичайний текст" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={() => exec("insertUnorderedList")} label="• —" title="Маркований список" />
        <Btn onClick={() => exec("insertOrderedList")} label="1." title="Нумерований список" />
        <Btn onClick={() => exec("formatBlock", "<blockquote>")} label="❝" title="Цитата" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={link} label="🔗" title="Посилання" />
        <Btn onClick={img} label="🖼" title="Картинка (URL)" />
        <Btn onClick={() => exec("removeFormat")} label="✕ форм." title="Очистити форматування" />
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        onBlur={e => onChange(e.currentTarget.innerHTML)}
        data-placeholder="HTML-вміст шаблону…"
        style={{ minHeight: 300, maxHeight: 520, padding: "14px 16px", fontSize: 13.5, lineHeight: 1.55, color: t.text, outline: "none", overflowY: "auto", fontFamily: "inherit" }} />
      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: ${t.textMuted}; pointer-events: none; }
        [contenteditable] h2 { font-size: 18px; font-weight: 700; margin: 8px 0; }
        [contenteditable] h3 { font-size: 15px; font-weight: 700; margin: 6px 0; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 22px; margin: 6px 0; }
        [contenteditable] blockquote { border-left: 3px solid ${t.border}; padding: 4px 12px; color: ${t.textMuted}; margin: 6px 0; font-style: italic; }
        [contenteditable] a { color: #6366f1; text-decoration: underline; }
        [contenteditable] p { margin: 4px 0; }
        [contenteditable] img { max-width: 100%; }
      `}</style>
    </div>
  );
}
